'use client';

import { useState, useEffect } from 'react';
import { Phone as PhoneIcon, X as XIcon, Navigation as NavigationIcon } from 'lucide-react';
import { CmsIcon } from '@/components/CmsIcon';

type Position = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

const ANCHOR: Record<Position, React.CSSProperties> = {
  'bottom-right': { bottom: '6rem', right: '1.6rem' },
  'bottom-left':  { bottom: '6rem', left:  '1.6rem' },
  'top-right':    { top:    '1.6rem', right: '1.6rem' },
  'top-left':     { top:    '1.6rem', left:  '1.6rem' },
};

export interface SpeedDialPhone   { number: string; label?: string | null }
export interface SpeedDialSocial  { platform: string; url: string }

interface Props {
  number?: string | null;
  message?: string | null;
  position?: Position | null;
  phones?: SpeedDialPhone[];
  socialLinks?: SpeedDialSocial[];
  directionUrl?: string | null;
}

const PLATFORM_BG: Record<string, string> = {
  instagram:   'radial-gradient(circle at 30% 110%, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
  facebook:    '#1877F2',
  twitter:     '#000',
  x:           '#000',
  youtube:     '#FF0000',
  linkedin:    '#0A66C2',
  tripadvisor: '#34E0A1',
  tiktok:      '#010101',
  pinterest:   '#E60023',
};

const FAB_SIZE  = '3.375rem'; /* 54px  — social/contact items */
const TRIG_SIZE = '5rem';     /* 80px  — main trigger button  */

/* Styles for .chaty-label, .chaty-hello, etc. are in globals.css */


/* ── FAB item ────────────────────────────────────────────────── */
function FabItem({
  href, label, bg, icon, index, open, expandUp, onRight,
}: {
  href: string; label: string; bg: string; icon: React.ReactNode;
  index: number; open: boolean; expandUp: boolean; onRight: boolean;
}) {
  /* 5.5rem clears the 5rem trigger + 0.5rem gap; then 4rem steps per item */
  const offset = 5.5 + index * 4;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="chaty-item"
      style={{
        position: 'absolute',
        [expandUp ? 'bottom' : 'top']: open ? `${offset}rem` : '0',
        left: '50%',
        transform: 'translateX(-50%)',
        opacity: open ? 1 : 0,
        pointerEvents: open ? 'auto' : 'none',
        transition: `bottom 0.3s ease ${index * 0.05}s, top 0.3s ease ${index * 0.05}s, opacity 0.25s ease ${index * 0.05}s`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: FAB_SIZE,
        height: FAB_SIZE,
        borderRadius: '50%',
        background: bg,
        boxShadow: '0 3px 12px rgba(0,0,0,0.25)',
        textDecoration: 'none',
        flexShrink: 0,
      }}
    >
      <span className={`chaty-label chaty-label-${onRight ? 'right' : 'left'}`}>
        {label}
      </span>
      {icon}
    </a>
  );
}

/* ── Chat bubble SVG ─────────────────────────────────────────── */
function ChatBubbleIcon() {
  return (
    <svg viewBox="-496.8 507.1 54 54" width="32" height="32" aria-hidden="true">
      <path fill="#ffffff"
        d="M-459.5,523.5H-482c-2.1,0-3.7,1.7-3.7,3.7v13.1c0,2.1,1.7,3.7,3.7,3.7h19.3l5.4,5.4c0.2,0.2,0.4,0.2,0.7,0.2c0.2,0,0.2,0,0.4,0c0.4-0.2,0.6-0.6,0.6-0.9v-21.5C-455.8,525.2-457.5,523.5-459.5,523.5z"
      />
      <path fill="none" stroke="#808080" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
        d="M-476.5,537.3c2.5,1.1,8.5,2.1,13-2.7"
      />
      <path fill="none" stroke="#808080" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
        d="M-460.8,534.5c-0.1-1.2-0.8-3.4-3.3-2.8"
      />
    </svg>
  );
}

/* ── Main export ─────────────────────────────────────────────── */
export default function WhatsAppButton({
  number, message, position, phones = [], socialLinks = [], directionUrl,
}: Props) {
  const [open,      setOpen]      = useState(false);
  const [spin,      setSpin]      = useState(0);
  const [showHello, setShowHello] = useState(false);

  /* Show "Hello!" bubble once per browser session */
  useEffect(() => {
    if (!localStorage.getItem('chaty_greeted')) {
      setShowHello(true);
    }
  }, []);

  const handleToggle = () => {
    setSpin(s => s + 1);
    setOpen(o => !o);
    setShowHello(false);
  };

  const dismissHello = () => {
    setShowHello(false);
    localStorage.setItem('chaty_greeted', '1');
  };

  const pos      = position || 'bottom-right';
  const expandUp = pos.startsWith('bottom');
  const onRight  = pos.endsWith('right');

  const items: { href: string; label: string; bg: string; icon: React.ReactNode }[] = [];

  if (number) {
    const clean = number.replace(/\s+/g, '').replace(/^\+/, '');
    const waUrl = `https://wa.me/${clean}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
    items.push({
      href: waUrl, label: 'WhatsApp', bg: '#25D366',
      icon: <CmsIcon name="tabler:brand-whatsapp" size={22} color="white" strokeWidth={1.5} />,
    });
  }

  if (phones.length > 0) {
    items.push({
      href: `tel:${phones[0].number.replace(/\s+/g, '')}`,
      label: phones[0].label || 'Call Us', bg: '#128C7E',
      icon: <PhoneIcon size={20} color="white" strokeWidth={1.8} />,
    });
  }

  for (const s of socialLinks) {
    const platform = s.platform.toLowerCase();
    const bg = PLATFORM_BG[platform] ?? '#555';
    items.push({
      href: s.url,
      label: s.platform.charAt(0).toUpperCase() + s.platform.slice(1),
      bg,
      icon: <CmsIcon name={`tabler:brand-${platform}`} size={22} color="white" strokeWidth={1.5} />,
    });
  }

  if (directionUrl) {
    items.push({
      href: directionUrl, label: 'Directions', bg: '#1A73E8',
      icon: <NavigationIcon size={20} color="white" strokeWidth={1.8} />,
    });
  }

  if (items.length === 0) return null;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          ...ANCHOR[pos],
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {open && (
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: -1 }}
            aria-hidden
          />
        )}

        <div style={{ position: 'relative', width: TRIG_SIZE, height: TRIG_SIZE }}>

          {/* ── Hello bubble — auto-shown once, click to dismiss ── */}
          {showHello && (
            <div
              className={`chaty-hello chaty-hello-${onRight ? 'right' : 'left'}`}
              onClick={dismissHello}
              role="button"
              aria-label="Dismiss greeting"
            >
              <span>Hello! 👋</span>
              <span style={{ opacity: 0.45, fontSize: '0.75rem', lineHeight: 1 }}>✕</span>
            </div>
          )}

          {/* ── FAB items (social / contact) ── */}
          {items.map((item, i) => (
            <FabItem
              key={item.href}
              href={item.href}
              label={item.label}
              bg={item.bg}
              icon={item.icon}
              index={i}
              open={open}
              expandUp={expandUp}
              onRight={onRight}
            />
          ))}

          {/* ── Trigger button ── */}
          <button
            onClick={handleToggle}
            aria-label={open ? 'Close contact menu' : 'Open contact menu'}
            className="chaty-trigger"
            style={{
              position: 'relative', zIndex: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: TRIG_SIZE, height: TRIG_SIZE,
              borderRadius: '50%',
              background: open ? '#FF6060':'#048c92',
              border: 'none', cursor: 'pointer',
              boxShadow: '0 4px 18px rgba(0,0,0,0.3)',
              transform: open ? 'scale(1.05)' : 'scale(1)',
              transition: 'background-color 0.3s ease, transform 0.2s ease'
            }}
          >
            <span className={`chaty-label chaty-label-${onRight ? 'right' : 'left'}`}>
              {open ? 'Hide' : 'Chat with us'}
            </span>

            {/* Both icons always in DOM — spin together, crossfade opacity */}
            <span style={{ position: 'relative', width: '32px', height: '32px', flexShrink: 0 }}>
              <span style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease',
                transform: `rotate(${spin * 360}deg)`,
                opacity: open ? 0 : 1,
              }}>
                <ChatBubbleIcon />
              </span>
              <span style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease',
                transform: `rotate(${spin * 360}deg)`,
                opacity: open ? 1 : 0,
              }}>
                <XIcon size={26} color="white" />
              </span>
            </span>
          </button>
        </div>
      </div>
    </>
  );
}
